/**
 * Copyright (c) 2026, CheckPlus Limited
 */

/**
 * Runtime dispatch shim for the two routing variants (main + subdomain).
 *
 * Replaces the pre-3g/1.5 model where this file was copied over from
 * either `src/routes-main.js` or `src/routes-subdomain.js` by
 * `checkplus_bash/routes_switcher.sh`. That model worked for single-
 * webpack-process workflows (interactive dev, prod builds) but broke
 * under Playwright's two-project setup, where both dev servers boot in
 * parallel and CRA's HMR watches `src/routes.js`. The second-to-finish
 * `routes_switcher.sh` invocation would mutate the file, and HMR on the
 * first webpack process would then hot-recompile with the wrong variant
 * — silently breaking any main-only or subdomain-only spec (e.g. the
 * `OrgLogin` mount-smoke in Milestone 3g/2). See the §7 3g/1.5 sub-
 * bullet for the diagnostic trail.
 *
 * The shim resolves the variant at build time via CRA's DefinePlugin
 * substitution of `process.env.REACT_APP_ROUTE_VARIANT`. Each webpack
 * process is given its own static value (set by `test_server.sh` per-
 * port or by `action_switcher.sh` per-invocation), so dead-code
 * elimination drops the unused variant from each build. No shared
 * mutable state between processes; no HMR cross-talk.
 *
 * Migration status (all three entry points migrated as of 2026-09-02):
 *   - `checkplus_bash/test_server.sh` — Playwright / CI. Exports
 *     REACT_APP_ROUTE_VARIANT per webServer invocation.
 *   - `checkplus_bash/action_switcher.sh` — interactive dev. Exports it
 *     per launch.
 *   - `checkplus_bash/routes_switcher.sh` — `npm run routes:*`, used by
 *     all seven deploy workflows. It cannot export into the separate
 *     `npm run build` step that follows it, so it writes the variable
 *     into the gitignored root `.env`, which CRA loads for both `build`
 *     and `start`. That is also what makes a bare `npm start` work.
 *
 * All three first restore this shim if it has been clobbered by a legacy
 * `cp src/routes-X.js src/routes.js`, using the same three-marker grep
 * (REACT_APP_ROUTE_VARIANT + routes-main + routes-subdomain). Detection
 * is deliberately "restore only when invalid" rather than an
 * unconditional rewrite, so the canonical documented shim in version
 * control survives and `git status` stays clean after a normal run.
 */

import routesMain from './routes-main';
import routesSubdomain from './routes-subdomain';

const variant = process.env.REACT_APP_ROUTE_VARIANT;
const known = variant === 'main' || variant === 'subdomain';

/*
 * A missing variant is a build misconfiguration, and it is reachable: the
 * value only gets into a `npm run build` through the line routes_switcher.sh
 * appends to the gitignored root `.env`, and `checkplus_bash/env_switcher.sh`
 * replaces that whole file (`cp env/.env-$myEnv .env`). So
 * `routes:subdomain && env:stage && build` drops it, and webpack builds that
 * happily. The deploy workflows order the two the safe way round today, but
 * only by step ordering.
 *
 * Throwing catches it immediately where somebody is watching, and is silent
 * sabotage where nobody is: at module load in a browser it renders nothing at
 * all, on a build that went green. So it throws in development and falls back
 * in production — a site on the wrong route tree is recoverable and obvious;
 * a white screen is neither.
 */
if (!known && process.env.NODE_ENV !== 'production') {
  throw new Error(
    `REACT_APP_ROUTE_VARIANT must be 'main' or 'subdomain'; ` +
      `got: ${JSON.stringify(variant)}. Set it via test_server.sh's -r ` +
      `flag or by exporting it before running react-app-rewired.`,
  );
}

const Routes = variant === 'subdomain' ? routesSubdomain : routesMain;

export default Routes;
